
import { computed, defineComponent, onMounted, reactive, ref } from 'vue'
import { ethers, providers } from 'ethers'
import { HappyRedPacketContract, RedPacketContract } from '@/contract/func/redPacket'
import Layout from './Layout.vue'
import BgSlot from '@/components/BgSlot.vue'
import CreateRedPack from '@/components/CreateRedPack.vue'
import TxLoading from '@/components/txLoading.vue'
import { connectWallet, getChainId, getProvider } from '@/composables/metamask'
import Share from '@/components/Home/Share.vue'
import { getTokenInfo } from '@/composables/fxwallet'
import { useRouter } from 'vue-router'
import { Http } from '@/plugins/axios'
import { generateAesCrypto, generateRandomByte, getNonceAPIHandler } from '@/composables/aes'
import { useStore } from 'vuex'
import { Chain } from '@/contract/rpc'
import type { State as WalletState } from '@/store/wallet'
import { HappyRedPacket } from '@/contract/helper/abi/HappyRedPacket'
import { Tools } from '@/plugins/tools'
import { useRecaptcha } from '@/composables/recaptcha'
import { useStorage } from '@/composables/storage'

type createRedPacketParams = Parameters<HappyRedPacket['create_red_packet']>

export default defineComponent({
  components: {
    Layout,
    BgSlot,
    CreateRedPack,
    TxLoading
    // Share
  },
  setup () {
    const store = useStore()
    const router = useRouter()
    const recaptcha = useRecaptcha()
    const storage = useStorage()

    const txLoading = ref(false)
    const tx = ref<ethers.providers.TransactionResponse>()

    const sendLoading = ref(false)

    const createStep = ref<'create' | 'share'>('create')

    const redPackId = ref('')

    const pageReady = ref(false)

    const tokenInfoInit = reactive({
      chainType: 0,
      contract: '',
      decimals: 0,
      originalSymbol: '',
      symbol: '',
      logo: ''
    })

    const createRedPackRef = ref()

    const provider = getProvider()

    const redPackContractAddrInit = ref('')

    const balanceInit = ref('')

    let redPackContract: RedPacketContract

    const redpackInfo = reactive({
      title: ''
    })

    const balance = computed(() => {
      return (tokenInfoInit.contract ? store.state.contractErc20.balance : store.state.wallet.balance) as string
    })

    const decimal = computed(() => {
      return (tokenInfoInit.contract ? store.state.contractErc20.info.decimal : store.getters['wallet/info'].decimal) as number
    })

    const chainId = computed(() => {
      return store.state.wallet.chainId as Chain
    })

    const account = computed(() => {
      return store.state.wallet.account as WalletState['account']
    })

    const redpackContract = computed(() => {
      return store.state.contractRedpack.contract as HappyRedPacketContract
    })

    const wallet = computed(() => {
      return store.state.wallet.wallet as WalletState['wallet']
    })

    function txData (opt: {
      _number?: createRedPacketParams['0'],
      _name?: createRedPacketParams['4'],
      _tokentype?: createRedPacketParams['5'],
      _tokenaddr?: createRedPacketParams['6'],
      _totaltokens?: createRedPacketParams['7']
    }) {
      const params = [
        opt._number,
        true,
        24 * 60 * 60,
        '',
        opt._name,
        opt._tokentype,
        opt._tokenaddr || `0x${'0'.repeat(40)}`,
        opt._totaltokens
      ] as Parameters<HappyRedPacket['create_red_packet']>

      return redpackContract.value.encode('create_red_packet', params)
    }

    async function CreationSuccessListener (...args: any[]) {
      const lastArg = args.at(-1)

      if (tx.value && lastArg.transactionHash === tx.value.hash) {
        redpackContract.value.contract.off('CreationSuccess', CreationSuccessListener)

        const id = args[1]

        const createTime: ethers.BigNumber = args[5]

        const duration: ethers.BigNumber = args[9]

        console.log(args)

        redPackId.value = id

        const captcha = await recaptcha.execute('submit')

        const msg = generateRandomByte().toString()

        const input = generateAesCrypto(msg)

        const nonce = await getNonceAPIHandler(id, chainId.value, captcha, input)

        const expire = createTime.add(duration).mul(1000).toNumber()

        storage.saveRedpackInput({
          id,
          msg,
          expire
        })

        router.push({
          name: 'share',
          query: {
            redPackId: id,
            chainId: tokenInfoInit.chainType,
            chainIdInit: chainId.value,
            nonce
          }
        })
      }
    }

    async function sendTx (txParams: providers.TransactionRequest) {
      if (wallet.value) {
        try {
          const { gasPrice, gasLimit } = await wallet.value.gasFee(txParams)

          tx.value = await wallet.value.signer.sendTransaction({
            ...txParams,
            gasPrice,
            gasLimit
          })

          txLoading.value = true

          redpackContract.value.contract.on('CreationSuccess', CreationSuccessListener)

          const txResult = await wallet.value.provider.waitForTransaction(tx.value.hash)

          if (txResult.status === 0) {
            throw txResult.logs
          }
        } catch (err) {
          console.log('createRedPackHandler', err)

          txLoading.value = false
        }
      }
    }

    async function createRedPackHandler (redPackData: {
      number: number;
      title: string;
      tokenContract: string;
      tokenType: 0 | 1;
      totalToken: string;
    }) {
      sendLoading.value = true

      redpackInfo.title = redPackData.title.substring(1)

      const totalToken = ethers.utils.parseUnits(redPackData.totalToken, decimal.value)

      const data = txData({
        _name: redPackData.title,
        _number: redPackData.number,
        _tokenaddr: tokenInfoInit.contract,
        _tokentype: tokenInfoInit.contract ? 1 : 0,
        _totaltokens: totalToken
      })

      const txParams: providers.TransactionRequest = {
        to: redpackContract.value.contract.address,
        data,
        value: tokenInfoInit.contract ? 0 : totalToken
      }

      await sendTx(txParams)

      sendLoading.value = false
    }

    onMounted(async () => {
      const tokenInfo = await getTokenInfo()

      tokenInfoInit.contract = tokenInfo.contract
      tokenInfoInit.chainType = tokenInfo.chainType
      tokenInfoInit.decimals = tokenInfo.decimals
      tokenInfoInit.originalSymbol = tokenInfo.originalSymbol
      tokenInfoInit.symbol = tokenInfo.symbol
      tokenInfoInit.logo = tokenInfo.logo

      pageReady.value = true

      if (tokenInfo.contract) {
        await store.dispatch('contractErc20/init', {
          address: tokenInfo.contract,
          rpc: store.getters['wallet/rpc'],
          account: store.state.wallet.account.normal
        })
      }

      if (createRedPackRef.value) {
        await createRedPackRef.value.allowance()
        createRedPackRef.value.checkAllowance()
      }

      // const { address, chainId, redPackContractAddr, balance } =
      //   await connectWallet(tokenInfo.contract)

      // redPackContractAddrInit.value = redPackContractAddr
      // addressInit.value = address
      // chainIdInit.value = chainId
      // // balanceInit.value = balance

      // console.log('onMounted', balance, balanceInit.value, redPackContractAddr)

      // redPackContract = new RedPacketContract(provider, redPackContractAddr)

      // pageReady.value = true
    })

    return {
      account,
      txLoading,
      provider,
      redPackContractAddrInit,
      redpackContract,
      createRedPackHandler,
      createStep,
      tokenInfoInit,
      redPackId,
      sendLoading,
      balanceInit,
      balance,
      chainId,
      pageReady,
      redpackInfo,
      createRedPackRef
    }
  }
})
